import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

type MessageType = 'success' | 'error' | "warning" | null;

interface AlertContextType {
    message: string;
    title?: string | null;
    text?: string | null;
    messageType: MessageType;
    setMessage: (message: string, type: MessageType, title?: string, onCloseClick?: () => void, text?: string) => void;
    clearMessage: () => void;
    onCloseMessage: () => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const useMessage = () => {
    const context = useContext(AlertContext);
    if (!context) {
        throw new Error("useMessage must be used within a AlertProvider");
    }
    return context;
};

export const AlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [message, setMessage] = useState<string>("");
    const [title, setTitle] = useState<string | null>(null);
    const [text, setText] = useState<string | null>(null);
    const [messageType, setMessageType] = useState<MessageType>(null);
    const [onCloseClickButton, setOnCloseClickButton] = useState<(() => void) | null>(null);

    const handleSetMessage = useCallback((msg: string, type: MessageType, title?: string, onCloseClick?: () => void, text?: string) => {
        setMessage(msg);
        setTitle(title || null);
        setText(text || null);
        setMessageType(type);
        setOnCloseClickButton(() => onCloseClick || null); // Armazena a função opcional
    }, []);

    const clearMessage = useCallback(() => {
        setMessage("");
        setMessageType(null);
        setTitle(null);
        setText(null);
    }, []);

    const onCloseMessage = useCallback(() => {
        setMessage("");
        setMessageType(null);
        setTitle(null);
        setText(null);

        if (onCloseClickButton) {
            onCloseClickButton(); // Executa a função se existir
            setOnCloseClickButton(null); // Reseta a função para evitar reuso inesperado
        }
    }, [onCloseClickButton]);

    const value = useMemo(() => ({
        message,
        messageType,
        setMessage: handleSetMessage,
        clearMessage,
        onCloseMessage,
        title,
        text,
    }), [message, messageType, handleSetMessage, clearMessage, onCloseMessage, title, text]);

    return (
        <AlertContext.Provider value={value}>
            {children}
        </AlertContext.Provider>
    );
};
        